import type { FsRow } from "./fsentry";
import { len } from "./util";

// Selection and keyboard for one visible directory page.
// Row ids are strings. ".." is a row the list inserts; it is not a cache entry.
export function createListSelection(deps: {
  sorted: () => FsRow[];
  wrapper: () => HTMLElement | undefined;
  scrollToId: (id: string) => void;
  openFile: (e: FsRow) => void;
  dirUp: () => void;
  openDir: (name: string) => void;
}) {
  let selectedEntries = $state<Record<string, FsRow>>({});
  // plain number: the list re-renders from selectedEntries, not from this index
  let lastSelectedIdx = -1;
  // Where a selection of several rows starts, as in Windows: Shift + Up,
  // Shift + Down and Shift + click select from this row to the one they
  // arrive at. It is the row last selected without Shift.
  let anchorIdx = -1;

  function findEntryIdxById(id: string) {
    let idx = -1;
    for (let e of deps.sorted()) {
      ++idx;
      if (e.id == id) {
        return idx;
      }
    }
    return -1;
  }

  function findEntryById(id: string) {
    let idx = findEntryIdxById(id);
    if (idx >= 0) {
      return deps.sorted()[idx];
    }
    return null;
  }

  function getEntryFromContextMenuEvent(ev: MouseEvent) {
    let el: HTMLElement | null = ev.target as HTMLElement | null;
    while (el) {
      if (el.id == "fm-file-list-wrap") {
        return null;
      }
      let id = el.dataset.id;
      if (id) {
        return findEntryById(id);
      }
      el = el.parentElement;
    }
    return null;
  }

  function setNodeSelected(id: string, on: boolean) {
    const el = deps.wrapper()?.querySelector(`[data-id="${id}"]`);
    if (!el) {
      console.log("didn't find node where data-id is", id);
      return;
    }
    if (on) {
      el.classList.add("bg-sky-200");
    } else {
      el.classList.remove("bg-sky-200");
    }
  }

  function unselectAll() {
    for (let name in selectedEntries) {
      setNodeSelected(name, false);
    }
    selectedEntries = {};
  }

  function makeSingleSelected(idx: number) {
    const e = deps.sorted()[idx];
    const id = e.id;
    unselectAll();
    selectedEntries[id] = e;
    setNodeSelected(id, true);
    lastSelectedIdx = idx;
    anchorIdx = idx;
  }

  // Selects the rows from the anchor to idx and nothing else. The anchor
  // stays, so the next Shift + arrow grows or shrinks the same selection.
  function selectRangeTo(idx: number) {
    const sorted = deps.sorted();
    if (anchorIdx < 0 || anchorIdx >= len(sorted)) {
      anchorIdx = lastSelectedIdx >= 0 && lastSelectedIdx < len(sorted) ? lastSelectedIdx : idx;
    }
    let from = Math.min(anchorIdx, idx);
    let to = Math.max(anchorIdx, idx);
    // only what changes: the list can be long
    for (let id of Object.keys(selectedEntries)) {
      let i = findEntryIdxInRange(sorted, id, from, to);
      if (i < 0) {
        delete selectedEntries[id];
      }
    }
    for (let i = from; i <= to; i++) {
      let e = sorted[i];
      if (!(e.id in selectedEntries)) {
        selectedEntries[e.id] = e;
      }
    }
    lastSelectedIdx = idx;
  }

  // Returns the index of the row with the id if it is in from..to, else -1.
  function findEntryIdxInRange(sorted: FsRow[], id: string, from: number, to: number): number {
    for (let i = from; i <= to; i++) {
      if (sorted[i].id == id) {
        return i;
      }
    }
    return -1;
  }

  function isSelected(idx: number) {
    const el = deps.sorted()[idx];
    return el.id in selectedEntries;
  }

  function openSelected(idx: number) {
    console.log("openSelected:", idx);
    let e = deps.sorted()[idx];
    if (!e.isDir) {
      deps.openFile(e);
      return;
    }
    if (e.name == "..") {
      deps.dirUp();
      return;
    }
    deps.openDir(e.name);
  }

  function markSelected(ev: MouseEvent, idx: number) {
    const isDblClick = ev.detail == 2;
    if (isDblClick) {
      openSelected(idx);
      return;
    }

    if (ev.shiftKey) {
      // from the anchor to the clicked row
      selectRangeTo(idx);
    } else if (ev.ctrlKey || ev.metaKey) {
      // adds the row to the selection or takes it out
      const e = deps.sorted()[idx];
      const id = e.id;
      const on = id in selectedEntries;
      if (on) {
        delete selectedEntries[id];
        lastSelectedIdx = -1;
      } else {
        selectedEntries[id] = e;
        lastSelectedIdx = idx;
      }
      setNodeSelected(id, !on);
      // the next Shift selection starts here
      anchorIdx = idx;
    } else {
      makeSingleSelected(idx);
    }
    ev.stopPropagation();
  }

  // where the mouse was when it last moved over a row
  let lastMouseX = -1;
  let lastMouseY = -1;

  // The row under the mouse becomes the selected one when the mouse moves,
  // as in the Home View: rows are not highlighted just for being under it.
  // Rows picked with Ctrl + click are left alone.
  function selectOnMouseMove(ev: MouseEvent, idx: number) {
    // the browser also sends this when the list moves under a still mouse,
    // as when the keys scroll it
    if (ev.screenX == lastMouseX && ev.screenY == lastMouseY) {
      return;
    }
    lastMouseX = ev.screenX;
    lastMouseY = ev.screenY;
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey) {
      return;
    }
    let n = len(Object.keys(selectedEntries));
    if (n > 1 || (n == 1 && lastSelectedIdx == idx)) {
      return;
    }
    makeSingleSelected(idx);
  }

  function handleKeyDown(ev: KeyboardEvent) {
    if (ev.key == "Backspace") {
      deps.dirUp();
      return;
    }
    if (len(deps.sorted()) == 0) {
      return;
    }

    if (ev.key == "Enter") {
      if (lastSelectedIdx != -1) {
        openSelected(lastSelectedIdx);
      }
      return;
    }
    let currIdx = lastSelectedIdx;
    // Shift + Up and Shift + Down select from the row they started at
    let isRange = ev.shiftKey && (ev.key == "ArrowUp" || ev.key == "ArrowDown");
    if (isRange && currIdx == -1) {
      // after Ctrl + click took a row out
      currIdx = anchorIdx;
    }
    let newIdx = currIdx;
    if (ev.key == "ArrowUp" || ev.key == "k") {
      if (currIdx == -1) {
        newIdx = 0;
      } else {
        newIdx--;
      }
      ev.stopPropagation();
      ev.preventDefault();
    } else if (ev.key == "ArrowDown" || ev.key == "j") {
      newIdx++;
      ev.stopPropagation();
      ev.preventDefault();
    } else {
      return;
    }
    const maxIdx = len(deps.sorted()) - 1;
    if (newIdx < 0) {
      newIdx = 0;
    } else if (newIdx >= maxIdx) {
      newIdx = maxIdx;
    }
    if (isRange) {
      selectRangeTo(newIdx);
    } else {
      makeSingleSelected(newIdx);
    }
    const e = deps.sorted()[newIdx];
    deps.scrollToId(e.id);
  }

  // Directory read replaces the row list, then selects the first row.
  function clearAndSelectFirst() {
    selectedEntries = {};
    lastSelectedIdx = -1;
    const sorted = deps.sorted();
    anchorIdx = -1;
    if (len(sorted) > 0) {
      lastSelectedIdx = 0;
      anchorIdx = 0;
      let e = sorted[0];
      selectedEntries[e.id] = e;
    }
  }

  // Returns the row the keys are on, or null: the one last selected, else
  // the first of the selected ones.
  function currentEntry(): FsRow | null {
    let sorted = deps.sorted();
    let e = lastSelectedIdx >= 0 ? sorted[lastSelectedIdx] : undefined;
    if (e && e.id in selectedEntries) {
      return e;
    }
    for (let row of sorted) {
      if (row.id in selectedEntries) {
        return row;
      }
    }
    return null;
  }

  function selectionIds() {
    return Object.keys(selectedEntries);
  }

  // Re-bind saved ids onto the new rows. Does not clear the first-row selection.
  function restoreIds(ids: string[]) {
    console.log("restoreSelection");
    for (let id of ids) {
      let idx = findEntryIdxById(id);
      if (idx >= 0) {
        selectedEntries[id] = deps.sorted()[idx];
        setNodeSelected(id, true);
      }
    }
  }

  return {
    get selected() {
      return selectedEntries;
    },
    findEntryIdxById,
    findEntryById,
    getEntryFromContextMenuEvent,
    makeSingleSelected,
    isSelected,
    markSelected,
    selectOnMouseMove,
    handleKeyDown,
    clearAndSelectFirst,
    currentEntry,
    selectionIds,
    restoreIds,
  };
}
